import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { SimpleTagDirectory } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link UpdateTagDirectoryDialog}
 */
export type UpdateTagDirectoryDialogProps = {
    /** The directory to update */
    directory: SimpleTagDirectory | undefined;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new tag
 */
export default function UpdateTagDirectoryDialog(props: UpdateTagDirectoryDialogProps) {
    const [t] = useTranslation("dialog-tag-directory");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: props.directory?.name ?? "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.directory) return;
                const res = await ApiInternal.tags.updateDirectory(props.directory.uuid, {
                    name: value.name,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error ? t("error.name-not-unique") : undefined,
                            description: undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            props.onClose();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.directory]);

    if (!props.directory) return null;

    return (
        <Dialog open={true} onClose={props.onClose}>
            <DialogTitle>{t("heading.update-tag-directory", { directory: props.directory.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            maxLength={255}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.update-tag-directory")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
